import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { capFirstChar } from 'src/helpers/utilities';
import { CAMERA_MODE_MOTION_ZONES } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
//Types
import type {
	CameraWebhookMode,
	CameraWebhookSelection,
	WebHooksParams,
	WebHookSupportedList,
} from 'src/types/requests/settings/webhooks';
import type { CameraMotionZone } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';

export const CAMERA_WEBHOOK_MOTION_MODE = 'camera_motion';
export const CAMERA_WEBHOOK_PRIVACY_MODE = 'camera_privacy';
export const CAMERA_WEBHOOK_ARM_MODE = 'camera_arm';

type FormatFn = (key: string, options?: { default?: string; values?: Record<string, string> }) => string;

const label = (formatFn: FormatFn, key: string, defaultValue: string, values?: Record<string, string>) =>
	capFirstChar(formatFn(key, { default: defaultValue, values }));

export const getCameraWebhookZoneComponent = (zone: string) => `camerazone:${zone.replace('zone_', '')}`;

export const getCameraWebhookZoneName = (zone: string, motionZones: CameraMotionZone[]) => {
	const $format = get(format);
	return (
		motionZones.find((item) => item.value === zone)?.title ||
		zone.replace('zone_', $format('text_zone', { default: 'Zone' }) + ' ')
	);
};

export const getCameraWebhookInitialSelection = (
	actionProps: WebHooksParams,
): CameraWebhookSelection => {
	const event = actionProps.event || '';
	let selectedMode = CAMERA_WEBHOOK_MOTION_MODE;
	if (event.startsWith('camerazone.')) {
		selectedMode = CAMERA_MODE_MOTION_ZONES;
	} else if (event.startsWith('camera.privacy_')) {
		selectedMode = CAMERA_WEBHOOK_PRIVACY_MODE;
	} else if (/^camera\.(armed|disarmed)$/.test(event)) {
		selectedMode = CAMERA_WEBHOOK_ARM_MODE;
	}

	return {
		selectedMode,
		selectedZone: selectedMode === CAMERA_MODE_MOTION_ZONES ? `zone_${actionProps.cid}` : '',
		selectedState: actionProps.event?.split('.').pop() || '',
	};
};

export const getCameraWebhookModes = (
	formatFn: FormatFn,
	cameraComponent: string | undefined,
	motionZones: CameraMotionZone[],
	selectedZone: string,
): CameraWebhookMode[] => {
	const zoneName = capFirstChar(getCameraWebhookZoneName(selectedZone, motionZones));

	return [
		{
			id: CAMERA_WEBHOOK_MOTION_MODE,
			tokenId: 'motion',
			title: label(formatFn, 'collapse_title_motion', 'Motion Detection'),
			heading: label(formatFn, 'title_scene_condition_camera_motion', 'When camera motion is'),
			component: cameraComponent,
			states: [
				{
					id: 'motion',
					event: 'camera.motion',
					title: label(formatFn, 'option_camera_motion', 'Motion detected'),
					icon: 'fas fa-person-walking',
				},
				{
					id: 'motion_end',
					event: 'camera.motion_end',
					title: label(formatFn, 'option_camera_motion_end', 'Motion stopped'),
					icon: 'fas fa-person',
				},
			],
		},
		{
			id: CAMERA_WEBHOOK_PRIVACY_MODE,
			tokenId: 'privacy',
			title: label(formatFn, 'camera_privacy_mode', 'Privacy Mode'),
			heading: label(formatFn, 'title_scene_condition_camera_privacy', 'When privacy mode is'),
			component: cameraComponent,
			states: [
				{
					id: 'privacy_on',
					event: 'camera.privacy_on',
					title: label(formatFn, 'option_camera_privacy_on', 'Privacy mode enabled'),
					icon: 'fas fa-video-slash',
				},
				{
					id: 'privacy_off',
					event: 'camera.privacy_off',
					title: label(formatFn, 'option_camera_privacy_off', 'Privacy mode disabled'),
					icon: 'fas fa-video',
				},
			],
		},
		{
			id: CAMERA_WEBHOOK_ARM_MODE,
			tokenId: 'arm',
			title: label(formatFn, 'camera_arm_disarm', 'Arm / Disarm'),
			heading: label(formatFn, 'title_scene_condition_camera_arm', 'When camera is'),
			component: cameraComponent,
			states: [
				{
					id: 'armed',
					event: 'camera.armed',
					title: label(formatFn, 'camera_armed', 'Armed'),
					icon: 'fas fa-shield',
				},
				{
					id: 'disarmed',
					event: 'camera.disarmed',
					title: label(formatFn, 'camera_disarmed', 'Disarmed'),
					icon: 'fas fa-shield-halved',
				},
			],
		},
		{
			id: CAMERA_MODE_MOTION_ZONES,
			tokenId: 'motion-zones',
			title: label(formatFn, 'camera_motion_zones', 'Motion zones'),
			heading: label(formatFn, 'select_motion_state_for_zone', `Motion state for ${zoneName}`, {
				zoneName,
			}),
			component: selectedZone ? getCameraWebhookZoneComponent(selectedZone) : undefined,
			usesZones: true,
			states: [
				{
					id: 'motion',
					event: 'camerazone.motion',
					title: label(formatFn, 'option_camerazone_motion', 'Motion detected in zone'),
					icon: 'fas fa-person-walking',
				},
				{
					id: 'motion_end',
					event: 'camerazone.motion_end',
					title: label(formatFn, 'option_camerazone_motion_end', 'Motion stopped in zone'),
					icon: 'fas fa-person',
				},
			],
		},
	];
};

export const isCameraWebhookEventSupported = (
	event: string,
	supportedActionsList: WebHookSupportedList,
) => Boolean(supportedActionsList?.types?.[event]);

export const getAvailableCameraWebhookModes = (
	modes: CameraWebhookMode[],
	motionZones: CameraMotionZone[],
	supportedActionsList: WebHookSupportedList,
) =>
	modes.filter(
		(mode) =>
			mode.states.some((state) =>
				isCameraWebhookEventSupported(state.event, supportedActionsList),
			) &&
			(!mode.usesZones || motionZones.length),
	);

export const getAvailableCameraWebhookStates = (
	mode: CameraWebhookMode | undefined,
	supportedActionsList: WebHookSupportedList,
) =>
	mode?.states.filter((state) => isCameraWebhookEventSupported(state.event, supportedActionsList)) ||
	[];
